import {
  Bell,
  BellRing,
  Clock,
  FileHeart,
  FileText,
  LayoutGrid,
  Link2,
  List,
  Star,
  type LucideIcon,
} from "lucide-react";
import {
  WidgetConfig,
  WidgetType,
  getDefaultDynamicListConfig,
  getDefaultFavoriteDocumentsConfig,
  getDefaultFavoriteSearchesConfig,
  getDefaultNotificationsConfig,
  getDefaultRecentSearchesConfig,
  getDefaultShortcutConfig,
  getDefaultStaticListConfig,
  getDefaultSubscriptionsConfig,
} from "@/models/dashboard/WidgetConfig";

interface WidgetTypeInfo {
  icon: LucideIcon;
  /** The type's name: the gallery entry, the widget's type tag and a new widget's default name. */
  label: string;
  description: string;
}

/** Every widget type the editor can add (LAR-726). */
export const WIDGET_TYPE_INFO: Record<WidgetType, WidgetTypeInfo> = {
  "static-list": {
    icon: FileText,
    label: "Liste",
    description:
      "Udvalgte kilder og workspace-dokumenter i den rækkefølge, du vælger.",
  },
  "dynamic-list": {
    icon: List,
    label: "Dynamisk liste",
    description:
      "Automatisk feed ud fra filtre, fx de nyeste afgørelser eller alt nyt i et workspace.",
  },
  shortcut: {
    icon: Link2,
    label: "Genveje",
    description: "Links til eksterne sider og steder i LODA.",
  },
  subscriptions: {
    icon: Bell,
    label: "Abonnementer",
    description: "Dine aktive abonnementer",
  },
  notifications: {
    icon: BellRing,
    label: "Notifikationer",
    description: "Notifikationshistorik",
  },
  "favorite-searches": {
    icon: Star,
    label: "Favoritsøgninger",
    description: "Dine gemte søgninger",
  },
  "recent-searches": {
    icon: Clock,
    label: "Seneste søgninger",
    description: "Dine seneste søgninger",
  },
  "favorite-documents": {
    icon: FileHeart,
    label: "Favoritdokumenter",
    description: "Dine favoritter",
  },
};

const UNKNOWN_WIDGET_TYPE: WidgetTypeInfo = {
  icon: LayoutGrid,
  label: "Widget",
  description: "",
};

/**
 * The type's info, for a type read from a stored dashboard or template. That
 * JSON can carry a type this build does not know; it gets a generic name
 * instead of breaking the grid.
 */
export const getWidgetTypeInfo = (type: WidgetType): WidgetTypeInfo =>
  (WIDGET_TYPE_INFO as Partial<Record<string, WidgetTypeInfo>>)[type] ??
  UNKNOWN_WIDGET_TYPE;

/** The gallery's "Indhold" section: widgets the administrator fills. */
export const CONTENT_WIDGET_TYPES: WidgetType[] = [
  "static-list",
  "dynamic-list",
  "shortcut",
];

/** The gallery's "Dine data" section: widgets that show the viewer's own data. */
export const USER_DATA_WIDGET_TYPES: WidgetType[] = [
  "subscriptions",
  "notifications",
  "favorite-searches",
  "recent-searches",
  "favorite-documents",
];

export const getDefaultWidgetConfig = (type: WidgetType): WidgetConfig => {
  switch (type) {
    case "dynamic-list":
      return getDefaultDynamicListConfig();
    case "static-list":
      return getDefaultStaticListConfig();
    case "shortcut":
      return getDefaultShortcutConfig();
    case "subscriptions":
      return getDefaultSubscriptionsConfig();
    case "notifications":
      return getDefaultNotificationsConfig();
    case "favorite-searches":
      return getDefaultFavoriteSearchesConfig();
    case "recent-searches":
      return getDefaultRecentSearchesConfig();
    case "favorite-documents":
      return getDefaultFavoriteDocumentsConfig();
  }
};
